import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface EffectiveFilter {
  entity: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'in' | 'not_in';
  values: string[];
}

export interface EffectiveFiltersResponse {
  isFullAccess: boolean;
  filters: EffectiveFilter[];
}

// ─── Query Keys ─────────────────────────────────────────────────────────────────

export const effectiveFiltersKeys = {
  all: ['data-authorization', 'effective-filters'] as const,
};

// ─── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch the current user's effective data role filters.
 *
 * Used to filter dropdown options in forms — only shows values
 * that the user is allowed to see based on their data role assignments.
 *
 * Provides helpers:
 * - `isValueAllowed(entity, field, value)` — returns true if the value is permitted
 * - `getFilterValues(entity, field)` — returns allowed values array, or undefined if unrestricted
 * - `isFullAccess` — true if the user has no data restrictions
 *
 * Usage:
 * ```tsx
 * const { isValueAllowed, getFilterValues, isFullAccess } = useEffectiveFilters();
 *
 * // Filter dropdown options
 * const allowedEntityTypes = getFilterValues('contacts', 'entity_type_id');
 * options={entityTypes.filter(et => !allowedEntityTypes || allowedEntityTypes.includes(et.id))}
 *
 * // Check a single value
 * if (isValueAllowed('contacts', 'entity_type_id', someId)) { ... }
 * ```
 */
export function useEffectiveFilters() {
  const { data, isLoading, error } = useQuery({
    queryKey: effectiveFiltersKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<EffectiveFiltersResponse>>(
        '/data-authorization/effective-filters',
      );
      return response.data.data ?? { isFullAccess: true, filters: [] };
    },
    staleTime: 60 * 1000, // 60 seconds — matches backend Redis cache TTL
    gcTime: 5 * 60 * 1000,
  });

  const isFullAccess = data?.isFullAccess ?? true;
  const filters = data?.filters ?? [];

  /**
   * Get the allowed values for a specific entity/field combination.
   * Returns undefined if no filter restriction exists (all values are allowed).
   */
  function getFilterValues(entity: string, field: string): string[] | undefined {
    if (isFullAccess) return undefined;

    const filter = filters.find((f) => f.entity === entity && f.field === field);
    if (!filter) return undefined;

    return filter.values;
  }

  /**
   * Check if a specific value is allowed for a given entity/field.
   * Returns true if:
   * - User has full access, OR
   * - No filter exists for that entity/field (unrestricted), OR
   * - The value is in the allowed values list
   */
  function isValueAllowed(entity: string, field: string, value: string): boolean {
    if (isFullAccess) return true;

    const filter = filters.find((f) => f.entity === entity && f.field === field);
    if (!filter) return true;

    return filter.values.includes(value);
  }

  return {
    data,
    isLoading,
    error,
    isFullAccess,
    filters,
    getFilterValues,
    isValueAllowed,
  };
}
